import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Reveal, { EASE } from "../components/Reveal";

const STEPS = [
  {
    n: "01",
    title: "UNDERSTAND",
    points: ["Your business.", "Your customers.", "Your goals.", "Your economics.", "Your competitive environment."],
  },
  {
    n: "02",
    title: "DISCOVER",
    points: ["What makes you different.", "What customers actually value.", "What proof supports the story."],
  },
  {
    n: "03",
    title: "DEFINE",
    points: ["Your positioning.", "Your message.", "Your narrative.", "Your customer journey."],
  },
  {
    n: "04",
    title: "DESIGN",
    points: ["Turn that story into a visual and digital experience."],
  },
  {
    n: "05",
    title: "BUILD",
    points: ["Develop the website, integrations, forms, systems and automation."],
  },
  {
    n: "06",
    title: "MEASURE",
    points: ["Use analytics and customer behavior to understand what works."],
  },
];

export default function Method() {
  const [active, setActive] = useState(0);
  const step = STEPS[active];

  return (
    <section id="method" data-testid="method-section" className="relative bg-navy py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="max-w-3xl">
          <p className="font-mono text-xs font-semibold tracking-[0.3em] text-gold">THE AT DIGITAL METHOD</p>
          <h2 className="mt-5 font-display text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl">
            We don't start with code.
            <br />
            We start with your business.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="flex flex-wrap gap-3 lg:col-span-5 lg:flex-col lg:gap-2.5" role="tablist" aria-label="Method steps">
              {STEPS.map((s, i) => (
                <button
                  key={s.n}
                  role="tab"
                  aria-selected={active === i}
                  data-testid={`method-step-${s.n}`}
                  onClick={() => setActive(i)}
                  className={`group flex items-center gap-4 rounded-xl border px-5 py-4 text-left transition-colors duration-200 lg:w-full ${
                    active === i
                      ? "border-gold bg-gold/10"
                      : "border-navyline bg-ink/40 hover:border-gold/40"
                  }`}
                >
                  <span
                    className={`font-mono text-sm font-semibold transition-colors duration-200 ${
                      active === i ? "text-gold" : "text-slate-600 group-hover:text-slate-400"
                    }`}
                  >
                    {s.n}
                  </span>
                  <span
                    className={`font-display text-sm font-bold tracking-[0.16em] transition-colors duration-200 ${
                      active === i ? "text-white" : "text-slate-400 group-hover:text-slate-200"
                    }`}
                  >
                    {s.title}
                  </span>
                </button>
              ))}
            </div>
            <div className="relative min-h-[220px] overflow-hidden rounded-2xl border border-navyline bg-ink/50 p-8 sm:p-10 lg:col-span-7">
              <AnimatePresence mode="wait">
                <motion.div
                  key={active}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -14 }}
                  transition={{ duration: 0.25, ease: EASE }}
                >
                  <p className="font-mono text-5xl font-semibold text-gold/20">{step.n}</p>
                  <h3 className="mt-3 font-display text-2xl font-bold tracking-[0.12em] text-gold">{step.title}</h3>
                  <ul className="mt-5 space-y-2.5">
                    {step.points.map((p) => (
                      <li key={p} data-testid="method-step-content" className="flex items-start gap-3 text-base text-slate-300">
                        <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" aria-hidden="true" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <p
            data-testid="method-final-line"
            className="mt-16 text-center font-display text-xl font-bold tracking-[0.14em] text-white sm:text-2xl"
          >
            THE TECHNOLOGY SERVES <span className="text-gold-gradient">THE STORY.</span>
          </p>
        </Reveal>
      </div>
    </section>
  );
}
